import React, { useEffect, useMemo, useState } from "react";
import { FormProvider, useFieldArray, useForm, useWatch } from "react-hook-form";
import axiosInstance from "../services/api/axios";
import { toast } from "react-toastify";
import { Plus, Trash2 } from "lucide-react";
import { fetchPropertyConfigurations } from "../services/projects";
import { useUser } from "../context/UserContext";
import { FormModal, Field, FormSubmit, yupResolver } from "./form";
import { SecondaryButton } from "./Buttons";
import {
    manualPropertySchema,
    PROPERTY_STATE_OPTIONS,
} from "../services/projects/schemas";

const getPropertyTypeFromConfig = (config) => {
    if (!config || typeof config !== "object") return "";
    return config.propertyType ?? config.property_type ?? "";
};

/**
 * Single-property create/edit modal. The building + configuration selects
 * cascade: pick a building, configs for that building load, picking a config
 * derives the property type (read-only display field).
 */
function ManualUpload({ projectId, toggleDialog, propertyToEdit, onSubmit, phaseId }) {
    const { userInfo } = useUser();
    const customerId =
        userInfo?.loginIdentifierDetails?.customer?.customerId || userInfo?.customerId;
    const isEdit = !!propertyToEdit;

    const [buildings, setBuildings] = useState([]);
    const [configurations, setConfigurations] = useState([]);
    const [loading, setLoading] = useState(false);

    const defaultValues = useMemo(() => {
        const cp = propertyToEdit?.metadata?.carParking;
        const carParking = Array.isArray(cp)
            ? cp.map(String)
            : cp != null
                ? [String(cp)]
                : [];
        return {
            buildingId: propertyToEdit?.buildingData?.buildingId ?? "",
            propertyConfigId: propertyToEdit?.propertyConfigData?.propertyConfigId ?? 0,
            propertyState: propertyToEdit?.propertyData?.propertyState || "",
            propertyNo: propertyToEdit?.propertyData?.propertyNo || "",
            floor: propertyToEdit?.metadata?.floor ?? 0,
            floorRiseAmount: propertyToEdit?.metadata?.floorRise ?? 0,
            carParking,
        };
    }, [propertyToEdit]);

    const methods = useForm({
        defaultValues,
        mode: "onTouched",
        resolver: yupResolver(manualPropertySchema),
    });
    const { control, handleSubmit, reset, setValue } = methods;
    const { fields: parkingFields, append: appendParking, remove: removeParking } =
        useFieldArray({ control, name: "carParking" });

    useEffect(() => {
        reset(defaultValues);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [propertyToEdit]);

    // Fetch buildings on mount.
    useEffect(() => {
        const fetchBuildings = async () => {
            try {
                const response = await axiosInstance.get("/projects/buildings", {
                    params: { projectId, projectPhaseId: phaseId, customerId },
                });
                setBuildings(response || []);
            } catch (error) {
                console.error("Error fetching buildings:", error);
                toast.error("Failed to load buildings");
            }
        };
        fetchBuildings();
    }, [projectId, phaseId, customerId]);

    // Watch the building field, refetch configs whenever it changes.
    const selectedBuildingId = useWatch({ control, name: "buildingId" });
    useEffect(() => {
        if (!selectedBuildingId) {
            setConfigurations([]);
            return;
        }
        const fetchConfigs = async () => {
            setLoading(true);
            try {
                const response = await fetchPropertyConfigurations({
                    projectId,
                    customerId,
                    buildingId: selectedBuildingId,
                    projectPhaseId: phaseId,
                });
                const configData = Array.isArray(response?.data?.items)
                    ? response.data.items
                    : Array.isArray(response?.items)
                        ? response.items
                        : Array.isArray(response?.data)
                            ? response.data
                            : Array.isArray(response)
                                ? response
                                : [];
                setConfigurations(configData);
            } catch (error) {
                console.error("Error fetching configurations:", error);
                toast.error("Failed to load configurations");
                setConfigurations([]);
            } finally {
                setLoading(false);
            }
        };
        fetchConfigs();
    }, [selectedBuildingId, projectId, phaseId, customerId]);

    const selectedConfigId = useWatch({ control, name: "propertyConfigId" });
    const selectedConfig = useMemo(
        () =>
            configurations.find((c) => c.propertyConfigId === Number(selectedConfigId)) ||
            null,
        [configurations, selectedConfigId],
    );
    const derivedPropertyType =
        getPropertyTypeFromConfig(selectedConfig) ||
        getPropertyTypeFromConfig(propertyToEdit?.propertyConfigData) ||
        "";

    // When the user switches building, reset the config selection.
    useEffect(() => {
        if (selectedBuildingId !== defaultValues.buildingId) {
            setValue("propertyConfigId", 0, { shouldValidate: false });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedBuildingId]);

    // Floor cap warning vs the building's number of floors.
    const watchedFloor = useWatch({ control, name: "floor" });
    useEffect(() => {
        const maxFloors = selectedConfig?.building?.numberOfFloors;
        if (maxFloors && Number(watchedFloor) > maxFloors) {
            toast.warn(`Floor cannot be greater than ${maxFloors}`);
        }
    }, [watchedFloor, selectedConfig]);

    const buildingOptions = useMemo(
        () => buildings.map((b) => ({ value: b.buildingId, label: b.buildingName })),
        [buildings],
    );
    const configOptions = useMemo(
        () =>
            configurations.map((c) => ({
                value: c.propertyConfigId,
                label: c.configName,
            })),
        [configurations],
    );

    const submitProperty = async (data) => {
        try {
            const payload = {
                ...data,
                buildingId: parseInt(data.buildingId, 10),
                propertyConfigId: parseInt(data.propertyConfigId, 10),
                floor: parseInt(data.floor || 0, 10),
                floorRiseAmount: parseInt(data.floorRiseAmount || 0, 10),
                propertyType: derivedPropertyType,
                carParking: (data.carParking || []).filter((v) => v && v.trim() !== ""),
            };

            if (isEdit) {
                await axiosInstance.put(
                    `/property/manual?propertyId=${propertyToEdit?.propertyData?.propertyId}`,
                    payload,
                );
                toast.success("Property updated successfully");
            } else {
                if (payload.propertyConfigId === 0) {
                    toast.error("Please select a property configuration.");
                    return;
                }
                await axiosInstance.post("/property/manual", payload);
                toast.success("Property created successfully");
            }
            toggleDialog();
            onSubmit?.();
        } catch (error) {
            console.error("Error making API requests:", error);
            toast.error(
                error?.detail ||
                    error?.response?.data?.detail ||
                    error?.response?.data?.message ||
                    "Failed to create/update property",
            );
        }
    };

    return (
        <FormModal
            open
            onClose={toggleDialog}
            title={isEdit ? "Edit Property" : "Property Details"}
            size="lg"
        >
            <FormProvider {...methods}>
                <form
                    onSubmit={handleSubmit(submitProperty)}
                    className="space-y-4"
                    noValidate
                >
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <Field
                            name="buildingId"
                            label="Building"
                            type="select"
                            options={buildingOptions}
                            placeholder="Select Building"
                            required
                        />
                        <Field
                            name="propertyConfigId"
                            label="Configuration"
                            type="select"
                            options={configOptions}
                            placeholder={
                                !selectedBuildingId ? "Pick a building first" :
                                loading ? "Loading..." : "Select Configuration"
                            }
                            required
                            isDisabled={!selectedBuildingId || loading}
                        />
                        {/* Property type is derived from the selected config — display only. */}
                        <div>
                            <label className="block text-sm font-normal text-slate-500 mb-1">
                                Property Type
                            </label>
                            <input
                                type="text"
                                readOnly
                                value={derivedPropertyType}
                                placeholder={
                                    selectedConfigId ? "" : "Select a configuration"
                                }
                                className="w-full px-2 py-2 border border-slate-300 rounded-md bg-slate-50 text-sm focus:outline-none"
                            />
                        </div>

                        <Field
                            name="propertyState"
                            label="Property State"
                            type="select"
                            options={PROPERTY_STATE_OPTIONS}
                            placeholder="Select Property State"
                        />
                        <Field name="propertyNo" label="Property No." />
                        <Field name="floor" label="Floor" type="number" />
                        <Field name="floorRiseAmount" label="Floor Rise Amount" type="number" />
                    </div>

                    {/* Dynamic car-parking inputs */}
                    <div>
                        <label className="block text-sm font-normal text-slate-500 mb-1">
                            Car Parking
                        </label>
                        <div className="space-y-1">
                            {parkingFields.map((field, index) => (
                                <div key={field.id} className="flex items-center gap-2">
                                    <input
                                        type="text"
                                        {...methods.register(`carParking.${index}`)}
                                        placeholder={`Car Parking ${index + 1}`}
                                        className="block w-full h-8 border border-slate-300 rounded-sm px-2 py-1 focus:border-flamingo-500 focus:ring-0 focus:outline-none text-sm"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => removeParking(index)}
                                        className="px-2 py-1 rounded bg-rose-500 text-white hover:bg-rose-600"
                                    >
                                        <Trash2 size={14} />
                                    </button>
                                </div>
                            ))}
                            <SecondaryButton
                                type="button"
                                onClick={() => appendParking("")}
                                icon={<Plus className="mr-2 size-4" />}
                                left
                                className="w-fit mt-1"
                            >
                                Add Car Parking
                            </SecondaryButton>
                        </div>
                    </div>

                    <FormSubmit loadingLabel={isEdit ? "Updating..." : "Saving..."}>
                        {isEdit ? "Update" : "Submit"}
                    </FormSubmit>
                </form>
            </FormProvider>
        </FormModal>
    );
}

export default ManualUpload;
